import { useMemo } from "react";
import { DeviceCommands, HardwareStatuses } from "@app/shared/src/api/api.gen";
import { Device } from "@app/shared/src/models";
import { useLoad } from "./useLoad";

/**
 * Which trucks ran their pre-deployment relay test after the last time they were powered on.
 *
 * A driver testing the lights and horn from the cab queues a relay-test command, so those rows are
 * the record of the check. "After power on" is the part that matters: a test from before the unit
 * was last booted says nothing about this shift, so each device's uptime is used to find its boot
 * time and only tests after it count.
 *
 * Only units that were actually powered up within the window are counted at all — a truck sitting
 * in the yard all week has nothing to test, and counting it as a miss would bury the trucks that
 * genuinely rolled out unchecked.
 */

const WINDOW_MS = 24 * 60 * 60 * 1000;

export type PreDeploymentState = {
    /** Devices booted within the window — the ones that could plausibly have deployed. */
    booted: Device[];
    /** Of those, the ones with a relay test after their boot. */
    tested: Device[];
    /**
     * Units that reported a usable startup time at all. Boot time comes from a hardware status a
     * unit may never send, so zero means "cannot tell", not "nothing was switched on".
     */
    reporting: number;
    isLoading: boolean;
};

export function usePreDeploymentTests(devices: Device[]): PreDeploymentState {
    const deviceKey = devices.map((d) => d.id).join(",");

    const tests = useLoad(
        () => DeviceCommands.getRecentRelayTests(new Date(Date.now() - WINDOW_MS)),
        [],
    );

    // Boot time per device, derived from the newest hardware status: statusTimestamp - upTime.
    const boots = useLoad(async () => {
        const entries = await Promise.all(
            devices.map(async (d) => {
                try {
                    const rows = await HardwareStatuses.getRecentForDevice(d.id, 1);
                    const latest = rows?.[0];
                    if (!latest?.statusTimestamp || latest.upTime == null) return [d.id!, undefined] as const;
                    const bootedAt = new Date(latest.statusTimestamp).getTime() - latest.upTime * 1000;
                    return [d.id!, bootedAt] as const;
                } catch {
                    return [d.id!, undefined] as const;
                }
            }),
        );
        return new Map(entries);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceKey]);

    return useMemo(() => {
        const cutoff = Date.now() - WINDOW_MS;
        const bootMap = boots.data;
        const booted = devices.filter((d) => {
            const b = bootMap?.get(d.id!);
            return b !== undefined && b >= cutoff;
        });
        const tested = booted.filter((d) => {
            const bootedAt = bootMap!.get(d.id!)!;
            return (tests.data ?? []).some(
                (t) => t.deviceId === d.id && new Date(t.dateCreated ?? 0).getTime() >= bootedAt,
            );
        });
        const reporting = bootMap ? Array.from(bootMap.values()).filter((b) => b !== undefined).length : 0;
        return { booted, tested, reporting, isLoading: tests.isLoading || boots.isLoading };
    }, [devices, boots.data, boots.isLoading, tests.data, tests.isLoading]);
}
